export type MinimalUser = {
    id: string;
    username: string;
    avatarUrl?: string;
    discordUsername?: string;
    role: Role;
};

export type User = MinimalUser & {
    email: string;
    fullName?: string;
};

export type CreateUser = {
    username: string;
    discordUsername?: string;
    email: string;
    password: string;
    avatar?: File;
    /**
     * Optional at the type level so admin-driven creation paths can omit it;
     * the standard sign-up flow always provides one (defaulted to Male in the
     * UI). Persisted via supabase auth metadata and seeded into
     * `user_preferences.preferred_gender` on first profile creation.
     */
    preferredGender?: Gender;
};

export type UpdateUser = {
    id: User['id'];
    avatar?: File;
    username?: string;
    discordUsername?: string;
    password?: string;
    role: Role;
};

export type LoginUser = {
    email: string;
    password: string;
};

export const Role = {
    Player: 'Player',
    GM: 'GM',
    Admin: 'Admin',
    Owner: 'Owner',
    Bot: 'Bot',
} as const;
export type Role = typeof Role[keyof typeof Role];

export function isAdminRole(role: Role): boolean {
    return role === Role.Admin
        || role === Role.Owner
        || role === Role.Bot;
}

export const Gender = {
    Male: 'male',
    Female: 'female',
} as const;
export type Gender = typeof Gender[keyof typeof Gender];

export type UserPreferences = {
    userId: User['id'];
    notifyBeforeGameOpen: boolean;
    notifySpotsStillOpen: boolean;
    notifyBeforeGameStart: boolean;
    /**
     * GM-only opt-in: ping the game master when a player joins or leaves
     * one of their games. Only surfaced in Settings for users whose role
     * is GM/Admin/Owner/Bot; the trigger function double-checks the role
     * before inserting a notification.
     */
    notifyGameApplications: boolean;
    acceptedGmTc: boolean;
    /**
     * Tristate analytics consent. `null` means the user hasn't been asked yet
     * — `useAnalytics()` triggers the consent modal on first track attempt.
     */
    analyticsConsent: boolean | null;
    /**
     * Drives gendered copy in badge names, descriptions, and first-person
     * sentences (e.g. "Odigrao" vs "Odigrala"). Defaults to `Male` for new
     * rows; users can flip it in Settings.
     */
    preferredGender: Gender;
    /**
     * When true, the homepage carousels render as a single-column vertical
     * list instead of horizontal scrollers/grids. Off by default — users
     * opt in from Settings. Anonymous viewers always see the carousel UI.
     */
    preferVerticalLayout: boolean;
};

export type UpdateUserPreferences = Omit<UserPreferences, 'acceptedGmTc' | 'analyticsConsent'>;
